import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { fullChatTransport } from '../services/fullChatTransport';
import { chatService } from '../services/chatService';
import { authService } from '../services/authService';
import { ApiError, isAuthError, isRateLimitError } from '../services/errors';

const normalizeHistoryMessage = (message) => ({
  text: message?.text || message?.message || '',
  sender: message?.sender || message?.role || 'bot',
  timestamp: message?.timestamp || message?.created_at || new Date().toISOString(),
  system_data: message?.system_data,
  message_type: message?.message_type,
  offerAppleSignIn: message?.offerAppleSignIn,
  interactive_elements: message?.interactive_elements,
  actions: message?.actions,
});

const buildAuthRequiredElements = (event) => {
  const providers =
    Array.isArray(event?.providers) && event.providers.length > 0
      ? event.providers
      : ['google'];

  return providers.map((provider) => ({
    type: 'sign_in',
    provider,
    roleRequired: 'guest',
  }));
};

const useChatLogic = (initialChatId = null, onChatIdChange = null, isAuthenticated = false, options = {}) => {
  const transport = options.transport || fullChatTransport;
  const onFeatureDisabled = options.onFeatureDisabled;
  const disabledHandler = useRef(onFeatureDisabled);
  disabledHandler.current = onFeatureDisabled;
  const [input, setInput] = useState(options.initialDraft || '');
  const [sendStatus, setSendStatus] = useState('idle');
  const sendRequest = useRef();
  const uncertain = useRef();
  // Only server history and successful sends establish the reconciliation baseline.
  // Optimistic/rejected bubbles remain presentation state and cannot prove a send.
  const confirmedMessages = useRef(new Map());
  const reconciliationRequest = useRef();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [chatId, setChatId] = useState(initialChatId);
  const [chatError, setChatError] = useState('');
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState('');
  const [threadsNextCursor, setThreadsNextCursor] = useState(null);
  const [threadsHasMore, setThreadsHasMore] = useState(false);
  const activeRequestRef = useRef(null);
  const isMountedRef = useRef(true);
  const recoveryStore = options.recoveryStore;
  const recoveryOwner = sessionReady && sessionInfo?.session_id && options.recoveryIdentity
    ? JSON.stringify([options.recoveryIdentity, sessionInfo.session_id]) : null;
  const recoveryScope = recoveryOwner ? JSON.stringify([recoveryOwner, initialChatId]) : null;
  const restoredScope = useRef();
  const initializedScope = useRef();
  const [recoveredScope, setRecoveredScope] = useState(null);
  const recoveryReady = !recoveryStore || (recoveryScope && recoveryScope === recoveredScope);

  const abortActiveRequest = useCallback(() => {
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
      activeRequestRef.current = null;
    }
  }, []);

  const startNewRequest = useCallback(() => {
    abortActiveRequest();
    const controller = new AbortController();
    activeRequestRef.current = controller;
    return controller;
  }, [abortActiveRequest]);

  const updateChatId = useCallback(
    (newChatId) => {
      if (!newChatId || newChatId === chatId) return;
      setChatId(newChatId);
      onChatIdChange?.(newChatId);
    },
    [chatId, onChatIdChange]
  );

  useEffect(() => {
    sendRequest.current?.abort();
    reconciliationRequest.current?.abort();
    setIsLoading(false);
    setSendStatus('idle');
    uncertain.current = null;
    setChatError('');
    setChatId(initialChatId || null);
  }, [initialChatId]);

  useEffect(() => {
    if (!recoveryStore || !recoveryScope || restoredScope.current?.key === recoveryScope) return;
    if (restoredScope.current && restoredScope.current.owner !== recoveryOwner) {
      setMessages([]);
      confirmedMessages.current.clear();
      setChatError('');
      setIsAuthRequired(false);
    }
    const saved = !initialChatId && options.initialDraft ? null
      : recoveryStore.forOwner(recoveryOwner).get(initialChatId || 'new');
    // Adopting the ID just allocated for /chat keeps the current visible public draft.
    const adopting = restoredScope.current?.owner === recoveryOwner &&
      !restoredScope.current?.thread && initialChatId && initialChatId === chatId;
    if (!adopting) {
      setInput(saved?.input || (!initialChatId ? options.initialDraft || '' : ''));
      uncertain.current = saved?.attempt || null;
      setSendStatus(saved?.status || 'idle');
    }
    restoredScope.current = { key: recoveryScope, owner: recoveryOwner, thread: initialChatId };
    setRecoveredScope(recoveryScope);
  }, [recoveryStore, recoveryScope, recoveryOwner, initialChatId, chatId, options.initialDraft]);

  useEffect(() => {
    if (!recoveryStore || !recoveryOwner) return;
    return () => {
      abortActiveRequest();
      initializedScope.current = null;
      sendRequest.current?.abort();
      reconciliationRequest.current?.abort();
      setIsLoading(false); setIsChatLoading(false);
      setSendStatus((status) => ['sending', 'reconciling'].includes(status) ? 'uncertain' : status);
    };
  }, [recoveryStore, recoveryOwner, abortActiveRequest]);

  useEffect(() => {
    if (!recoveryStore || !recoveryReady || !recoveryOwner) return;
    const pending = ['sending', 'uncertain', 'reconciling', 'retry'].includes(sendStatus);
    recoveryStore.forOwner(recoveryOwner).set(chatId || initialChatId || 'new', {
      input: input || (pending ? uncertain.current?.text || '' : ''),
      attempt: pending && uncertain.current ? { ...uncertain.current } : null,
      // Aborting reception cannot establish whether the server committed the send.
      status: ['sending', 'reconciling'].includes(sendStatus) ? 'uncertain' : sendStatus,
    });
  }, [recoveryStore, recoveryReady, recoveryOwner, chatId, initialChatId, input, sendStatus]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortActiveRequest();
      sendRequest.current?.abort();
      reconciliationRequest.current?.abort();
    };
  }, [abortActiveRequest]);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        const session = await chatService.getSessionInfo();
        if (!isMountedRef.current) return;
        setSessionInfo(session);
        if (!session.session_id) {
          const newSession = await chatService.createSession();
          if (isMountedRef.current) setSessionInfo(newSession);
        }
      } catch (error) {
        if (!isMountedRef.current) return;
        if (error.code === 'FEATURE_DISABLED') { disabledHandler.current?.(); return; }
        try {
          const newSession = await chatService.createSession();
          if (isMountedRef.current) setSessionInfo(newSession);
        } catch (createError) {
          console.error('Error creating session:', createError);
          setChatError('Unable to start a session. Please try again later.');
        }
      } finally {
        if (isMountedRef.current) setSessionReady(true);
      }
    };

    initializeSession();
  }, []);

  const loadExistingChat = useCallback(
    async (targetChatId) => {
      if (!targetChatId) return;
      const controller = startNewRequest();
      setIsChatLoading(true);
      setChatError('');
      setMessages([]);
      try {
        const chat = await chatService.getChat(targetChatId, controller.signal);
        if (!isMountedRef.current || controller.signal.aborted) return;
        const resolvedChatId = chat?.chat_id || targetChatId;
        updateChatId(resolvedChatId);
        const history = await chatService.getChatHistory(resolvedChatId, controller.signal);
        if (!isMountedRef.current || controller.signal.aborted) return;
        const normalizedHistory = Array.isArray(history)
          ? history.map(normalizeHistoryMessage)
          : [];
        confirmedMessages.current.set(resolvedChatId, normalizedHistory);
        setMessages(normalizedHistory);
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error.code === 'FEATURE_DISABLED') { disabledHandler.current?.(); return; }
        const message = error instanceof Error ? error.message : String(error);
        setMessages([
          {
            text: `This chat is not available: ${message}`,
            sender: 'system',
            timestamp: new Date(),
          },
        ]);
        if (error instanceof ApiError && error.status === 403) {
          recoveryStore?.forOwner(recoveryOwner).delete(targetChatId);
          setInput(''); uncertain.current = null; setSendStatus('idle');
          setChatError('You are not authorized to view this chat.');
        } else if (isRateLimitError(error)) {
          setChatError('Chat history is temporarily unavailable due to rate limiting. Please try again later.');
        } else {
          setChatError('Unable to load this chat. Please check the link or try again later.');
        }
        setChatId(null);
      } finally {
        if (activeRequestRef.current === controller) {
          activeRequestRef.current = null;
          setIsChatLoading(false);
        }
      }
    },
    [startNewRequest, updateChatId, recoveryStore, recoveryOwner]
  );

  const loadThreadsPage = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (!isAuthenticated) {
        setThreads([]);
        setThreadsHasMore(false);
        setThreadsNextCursor(null);
        setThreadsError('');
        return;
      }
      setThreadsLoading(true);
      setThreadsError('');
      try {
        const payload = await chatService.listChats({ limit: 20, cursor });
        if (!isMountedRef.current) return;
        const nextItems = Array.isArray(payload?.items) ? payload.items : [];
        setThreads((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setThreadsHasMore(Boolean(payload?.has_more));
        setThreadsNextCursor(payload?.next_cursor || null);
      } catch (error) {
        if (!isMountedRef.current) return;
        if (error.code === 'FEATURE_DISABLED') { disabledHandler.current?.(); return; }
        if (isRateLimitError(error)) {
          setThreadsError('Chat history is temporarily unavailable due to rate limiting. Please try again later.');
        } else {
          setThreadsError('Unable to load chat history list.');
        }
      } finally {
        if (isMountedRef.current) {
          setThreadsLoading(false);
        }
      }
    },
    [isAuthenticated]
  );

  const loadMoreThreads = useCallback(async () => {
    if (!threadsHasMore || threadsLoading) return;
    await loadThreadsPage({ cursor: threadsNextCursor, append: true });
  }, [loadThreadsPage, threadsHasMore, threadsLoading, threadsNextCursor]);

  const openThread = useCallback(
    async (targetChatId) => {
      if (!targetChatId || targetChatId === chatId) return;
      await loadExistingChat(targetChatId);
    },
    [chatId, loadExistingChat]
  );

  const createNewChat = useCallback(
    async ({ maxRetries = 0 } = {}) => {
      setIsChatLoading(true);
      setChatError('');
      let lastError = null;

      for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
        const controller = startNewRequest();
        try {
          const chat = await chatService.createChat(controller.signal);
          if (!isMountedRef.current || controller.signal.aborted) {
            abortActiveRequest();
            setIsChatLoading(false);
            return null;
          }
          const newChatId = chat?.chat_id;
          if (!newChatId) {
            throw new Error('Chat creation did not return a chat id.');
          }
          if (chatId || initialChatId) {
            sendRequest.current?.abort(); reconciliationRequest.current?.abort();
            setInput(''); uncertain.current = null; setSendStatus('idle'); setIsLoading(false);
          }
          recoveryStore?.forOwner(recoveryOwner).delete('new');
          confirmedMessages.current.set(newChatId, []);
          updateChatId(newChatId);
          setMessages([]);
          abortActiveRequest();
          setIsChatLoading(false);
          return newChatId;
        } catch (error) {
          if (controller.signal.aborted) {
            abortActiveRequest();
            setIsChatLoading(false);
            return null;
          }
          if (error.code === 'FEATURE_DISABLED') { disabledHandler.current?.(); return null; }
          lastError = error;
        }
      }

      abortActiveRequest();
      setChatError(
        `Unable to start a new chat right now. ${
          lastError?.message ? `Reason: ${lastError.message}.` : ''
        } Please try again.`
      );
      setIsChatLoading(false);
      return null;
    },
    [abortActiveRequest, startNewRequest, updateChatId, chatId, initialChatId, recoveryStore, recoveryOwner]
  );

  useEffect(() => {
    if (!sessionReady || !recoveryReady) return;
    const scope = JSON.stringify([recoveryOwner, initialChatId]);
    if (initializedScope.current === scope || chatError) return;
    if (initialChatId) {
      initializedScope.current = scope;
      loadExistingChat(initialChatId);
    } else if (!chatId) {
      initializedScope.current = scope;
      createNewChat();
    }
  }, [sessionReady, recoveryReady, recoveryOwner, initialChatId, chatId, chatError, loadExistingChat, createNewChat]);

  useEffect(() => {
    if (!sessionReady) return;
    loadThreadsPage({ append: false });
  }, [sessionReady, isAuthenticated, loadThreadsPage, chatId]);

  const handleSendMessage = useCallback(async () => {
    if (recoveryReady && input.trim() && !['uncertain', 'reconciling'].includes(sendStatus) && !isLoading && !isChatLoading && chatId && !chatError && !isAuthRequired) {
      const userMessage = { text: input.trim(), sender: 'user', timestamp: new Date() };
      const controller = new AbortController(); sendRequest.current = controller;
      const pendingId = Symbol('pending reply');
      let completion; let eventError;
      uncertain.current = { text: userMessage.text, chatId,
        previousCount: (confirmedMessages.current.get(chatId) || []).filter((item) => item.sender === 'user' && item.text === userMessage.text).length };
      setSendStatus('sending');
      setMessages((prevMessages) => [...prevMessages, userMessage]);
      setInput('');
      setIsLoading(true);

      try {
        const transportResult = await transport.send({ message: userMessage.text, chatId,
          signal: controller.signal, onEvent: (event) => {
            if (controller.signal.aborted || !isMountedRef.current) return;
            if (event.type === 'delta') setMessages((previous) => {
              const current = previous.find((item) => item.pendingId === pendingId);
              return current ? previous.map((item) => item.pendingId === pendingId
                ? { ...item, text: item.text + event.text } : item)
                : [...previous, { text: event.text, sender: 'bot', pendingId, timestamp: new Date() }];
            });
            if (event.type === 'complete') completion = event.data;
            if (event.type === 'error') eventError = event.error || new Error('Response interrupted');
          } });
        if (controller.signal.aborted || !isMountedRef.current) return;
        if (eventError) throw eventError;
        const data = transportResult || completion;
        if (!data) throw new Error('Response interrupted');
        setMessages((previous) => previous.filter((item) => item.pendingId !== pendingId));
        setSendStatus('idle');
        uncertain.current = null;
        const confirmedChatId = data.chat_id || chatId;
        confirmedMessages.current.set(confirmedChatId, [
          ...(confirmedMessages.current.get(chatId) || []), userMessage,
        ]);
        if (data?.chat_id) {
          updateChatId(data.chat_id);
        }
        
        const uiEvents = Array.isArray(data?.ui_events) ? data.ui_events : [];
        const authRequiredEvent = uiEvents.find(e => e.type === 'AUTH_REQUIRED');
        const authEvent = authRequiredEvent || uiEvents.find(e => e.type === 'AUTH_SUGGESTED');
        if (authRequiredEvent) {
          setIsAuthRequired(true);
        }
        
        const botMessageText = authRequiredEvent
          ? 'Sign in to continue chatting'
          : data.reply || "Sorry, I couldn't understand the response.";
        const timestamp = data?.timestamp ? new Date(data.timestamp) : new Date();
        const hasApplePrompt = Boolean(
          data?.message_type === 'apple_signin_prompt' ||
            data?.system_data?.prompt_type === 'apple_signin' ||
            data?.apple_signin_offer === true ||
            data?.offerAppleSignIn === true ||
            (Array.isArray(data?.actions) && data.actions.includes('apple_signin'))
        );
        const interactiveElements = Array.isArray(data?.interactive_elements)
          ? data.interactive_elements
          : [];
        const botMessage = {
          text: botMessageText,
          sender: 'bot',
          timestamp,
          system_data: data?.system_data,
          message_type: data?.message_type,
          offerAppleSignIn: hasApplePrompt,
          interactive_elements: authEvent
            ? [...interactiveElements, ...buildAuthRequiredElements(authEvent)]
            : interactiveElements.length > 0
              ? interactiveElements
              : undefined,
          actions: Array.isArray(data?.actions) ? data.actions : undefined,
        };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
      } catch (error) {
        if (controller.signal.aborted || !isMountedRef.current) return;
        setInput(userMessage.text);
        if (error.code === 'FEATURE_DISABLED') {
          onFeatureDisabled?.(); setSendStatus('rejected'); return;
        }
        setSendStatus(error instanceof ApiError && error.status < 500 ? 'rejected' : 'uncertain');
        const message = error instanceof Error ? error.message : String(error);
        let text;
        if (message.includes('Failed to fetch')) {
          text = 'Unable to connect to the server. Please make sure the backend is running.';
        } else if (isAuthError(error)) {
          text = 'Your session has expired or you are not authorized. Please sign in and try again.';
          setIsAuthRequired(true);
        } else if (isRateLimitError(error)) {
          text = 'You have reached the rate limit. Please wait a moment before sending more messages.';
        } else {
          text = `Error: ${message}`;
        }
        const errorMessage = {
          text,
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) setIsLoading(false);
      }
    }
  }, [transport, onFeatureDisabled, recoveryReady, sendStatus, input, isLoading, isChatLoading, chatId, chatError, isAuthRequired, updateChatId]);

  const cancelResponse = useCallback(() => {
    sendRequest.current?.abort();
    setIsLoading(false);
    if (uncertain.current) {
      setInput(uncertain.current.text); setSendStatus('uncertain');
    }
  }, []);

  const reconcileSend = useCallback(async () => {
    const attempt = uncertain.current;
    if (!attempt || sendStatus === 'reconciling') return;
    const controller = new AbortController(); reconciliationRequest.current = controller;
    setSendStatus('reconciling');
    try {
      const history = await chatService.getChatHistory(attempt.chatId, controller.signal);
      if (!isMountedRef.current || controller.signal.aborted) return;
      if (!Array.isArray(history)) throw new Error('Invalid history');
      const normalized = history.map(normalizeHistoryMessage);
      const count = normalized.filter((item) => item.sender === 'user' && item.text === attempt.text).length;
      confirmedMessages.current.set(attempt.chatId, normalized);
      setMessages(normalized);
      if (count > attempt.previousCount) {
        setSendStatus('recorded'); setInput(''); uncertain.current = null;
      } else setSendStatus('retry');
    } catch (error) {
      if (controller.signal.aborted || !isMountedRef.current) return;
      if (error.code === 'FEATURE_DISABLED') { disabledHandler.current?.(); return; }
      setSendStatus('uncertain');
    }
  }, [sendStatus]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleAuthSignInError = useCallback((provider, error) => {
    const providerLabelMap = { apple: 'Apple', google: 'Google' };
    const providerLabel = providerLabelMap[provider] || 'Auth';
    console.error(`${providerLabel} Sign-In error:`, error);
    const baseMessage = error instanceof Error ? error.message : String(error);
    const text = isRateLimitError(error)
      ? `❌ ${providerLabel} Sign-In is temporarily unavailable due to rate limiting. Please try again later.`
      : `❌ ${providerLabel} Sign-In failed: ${baseMessage}`;
    const errorMessage = {
      text,
      sender: 'system',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, errorMessage]);
  }, []);

  const startAuthSignIn = useCallback(
    async (provider) => {
      try {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        const redirectUrl = authService.getSignInUrl(provider, { next: currentPath });

        window.location.assign(redirectUrl);
      } catch (error) {
        handleAuthSignInError(provider, error);
        throw error;
      }
    },
    [handleAuthSignInError]
  );

  const retryCreateChat = useCallback(() => {
    setChatError('');
    if (initialChatId) return loadExistingChat(initialChatId);
    return createNewChat();
  }, [createNewChat, initialChatId, loadExistingChat]);

  const startNewChat = useCallback(() => {
    setChatError('');
    return createNewChat();
  }, [createNewChat]);

  return useMemo(
    () => ({
      input,
      sendStatus,
      cancelResponse,
      reconcileSend,
      setInput,
      messages,
      isLoading,
      isChatLoading: isChatLoading || !recoveryReady,
      handleSendMessage,
      handleKeyDown,
      sessionInfo,
      chatId,
      chatError,
      retryCreateChat,
      startNewChat,
      startAuthSignIn,
      isAuthRequired,
      setIsAuthRequired,
      threads,
      threadsLoading,
      threadsError,
      threadsHasMore,
      loadMoreThreads,
      openThread,
    }),
    [
      input,
      sendStatus,
      cancelResponse,
      reconcileSend,
      messages,
      isLoading,
      isChatLoading,
      handleSendMessage,
      handleKeyDown,
      sessionInfo,
      recoveryReady,
      chatId,
      chatError,
      retryCreateChat,
      startNewChat,
      startAuthSignIn,
      isAuthRequired,
      setIsAuthRequired,
      threads,
      threadsLoading,
      threadsError,
      threadsHasMore,
      loadMoreThreads,
      openThread,
    ]
  );
};

export default useChatLogic;
